import { LanguageService } from 'services/language-service';
import { PosthogEvent, PosthogService } from 'services/posthog-service';
import {
  BillingSession,
  CreateBillingPortalSessionDto,
  CreateBillingPortalSessionInputDto,
  CreateCheckoutSessionDto,
  CreateCheckoutSessionInputDto,
  PostBillingSessionSummary
} from 'shared/interfaces/CustomerPortalSession';
import { ChurnDataDto, ChurnDiscount, Subscription, SubscriptionBalance } from 'shared/interfaces/Subscription';
import { axiosBaseInstance } from 'shared/utils/axios-instances';
import { buildBillingPortalReturnUrl } from 'shared/utils/navigation';

const subscriptionBaseEndpoint = '/subscription';

export const SubscriptionService = {
  getSubscription: async (): Promise<Subscription> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${subscriptionBaseEndpoint}`
    });

    return response.data;
  },
  getPostBillingSessionSummary: async (sessionCreatedAt: number): Promise<PostBillingSessionSummary> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${subscriptionBaseEndpoint}/post-billing-session-summary`,
      params: {
        sessionCreatedAt
      }
    });

    return response.data;
  },
  createBillingPortalSession: async (
    createBillingPortalSessionInputDto?: CreateBillingPortalSessionInputDto
  ): Promise<BillingSession> => {
    PosthogService.capture(PosthogEvent.SUBSCRIPTION_BILLING_SESSION_OPENED, createBillingPortalSessionInputDto);
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${subscriptionBaseEndpoint}/portal-session`,
      data: {
        ...createBillingPortalSessionInputDto,
        returnUrl: buildBillingPortalReturnUrl(createBillingPortalSessionInputDto?.redirectPath).toString(),
        locale: LanguageService.getCurrentLanguage(),
        updateSubscription: createBillingPortalSessionInputDto?.updateSubscription
      } as CreateBillingPortalSessionDto
    });

    return response.data;
  },
  startSubscriptionTrial: async (
    createCheckoutSessionInputDto: CreateCheckoutSessionInputDto
  ): Promise<BillingSession> => {
    PosthogService.capture(PosthogEvent.SUBSCRIPTION_TRIAL_BILLING_SESSION_OPENED, createCheckoutSessionInputDto);
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${subscriptionBaseEndpoint}/start-trial`,
      data: {
        returnUrl: buildBillingPortalReturnUrl(createCheckoutSessionInputDto.redirectPath).toString(),
        locale: LanguageService.getCurrentLanguage(),
        productId: createCheckoutSessionInputDto.productId
      } as CreateCheckoutSessionDto
    });

    return response.data;
  },
  getSubscriptionBalance: async (): Promise<SubscriptionBalance> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${subscriptionBaseEndpoint}/balance`
    });

    return response.data;
  },
  cancelSubscription: async (churnDataDto: ChurnDataDto): Promise<void> => {
    PosthogService.capture(PosthogEvent.SUBSCRIPTION_CANCELLED, churnDataDto);
    await axiosBaseInstance({
      method: 'post',
      url: `${subscriptionBaseEndpoint}/cancel`,
      data: churnDataDto
    });
  },
  reactivateSubscription: async (): Promise<void> => {
    PosthogService.capture(PosthogEvent.SUBSCRIPTION_REACTIVATED);
    await axiosBaseInstance({
      method: 'post',
      url: `${subscriptionBaseEndpoint}/reactivate`
    });
  },
  getChurnDiscount: async (churnDataDto: ChurnDataDto): Promise<ChurnDiscount> => {
    const response = await axiosBaseInstance({
      method: 'get',
      url: `${subscriptionBaseEndpoint}/churn-discount`,
      params: churnDataDto
    });

    return response.data;
  },
  claimChurnDiscount: async (churnDataDto: ChurnDataDto): Promise<void> => {
    PosthogService.capture(PosthogEvent.SUBSCRIPTION_CHURN_DISCOUNT_CLAIMED, churnDataDto);
    const response = await axiosBaseInstance({
      method: 'post',
      url: `${subscriptionBaseEndpoint}/claim-churn-discount`,
      data: churnDataDto
    });

    return response.data;
  }
};
